import { Check, Circle, Close, Delete, Edit } from '@mui/icons-material';
import { Box, Stack, Tooltip, Typography, Link as MuiLink, alpha } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useNotification } from '../../../../hooks/NotificationContext';
import useDebounce from '../../../../hooks/useDebounce';
import { getDisplayUserName, isExecutiveManager, showUserDisplayName } from '../../../../lib/userUtils';
import { fetchAssociatedTestCases } from '../../../../modules/associations';
import { bulkUpdateAssociations, removeAssociationsBulk } from '../../../../modules/testcycles';
import { testCycleTestCaseTableHeaders } from '../../test-case/testCaseTableHead';
import ActionButton from '../../../../ui/primitives/ActionButton';
import ActionMenu from '../../../../ui/primitives/ActionMenu';
import CustomSelect from '../../../../ui/primitives/CustomSelect';
import ConfirmationModal from '../../../../ui/primitives/ConfirmationModal';
import DetailsCard from '../../../../ui/layout/DetailsCard';
import TableComponent from '../../../../ui/data/table/TableComponent';
import { tokens } from '../../../../mui-theme';
import AddExistingTestCaseModal from './AddExistingTestCaseModal';
import LinkedTestCaseActions from './LinkedTestCaseActions';

const LinkedTestCasesComponent = ({
  entityId,
  users,
  data: testCycle,
  fetchTestCycleData,
}) => {
  const { projectId } = useParams();
  const { addNotification } = useNotification();

  const [data, setData] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [originalTotalCount, setOriginalTotalCount] = useState(0);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchQuery = useDebounce(searchValue, 500);
  const [selectedTestSuite, setSelectedTestSuite] = useState('all');

  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState('asc');
  const [editingRowId, setEditingRowId] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const isCompleted =
    testCycle?.completion_date !== null ||
    testCycle?.status?.name === 'Completed';

  const userOptions = useMemo(
    () => (users || []).map((u) => ({
      id: u.id,
      name: u?.full_name || u?.email || '',
      disabled: u?.status === 'inactive',
    })),
    [users],
  );

  const renderUserOption = useCallback(
    (opt) => getDisplayUserName(users?.find((u) => u.id === opt.id)),
    [users],
  );

  const fetchLinkedTestCases = async (pageNumber = 1, search = '', testSuite = 'all') => {
    setLoading(true);
    try {
      const response = await fetchAssociatedTestCases(
        entityId, projectId, search,
        testSuite !== 'all' ? [testSuite] : [],
        'Approved', true, pageNumber, perPage,
      );
      setData(response?.associations || []);
      setTotalCount(response?.total || 0);

      if (!search && testSuite === 'all') {
        setOriginalTotalCount(response?.total || 0);
      }

      setPage(pageNumber);
      if (response?.available_test_suites?.length > 0) {
        setTestSuites(response.available_test_suites);
      }
    } catch (error) {
      addNotification('danger', error?.message || 'Failed to fetch linked test cases.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinkedTestCases(page, debouncedSearchQuery, selectedTestSuite);
  }, [entityId, projectId, debouncedSearchQuery, selectedTestSuite, perPage, page]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handlePageChange = (newPage) => setPage(newPage);

  const handleSearch = (value) => { setSearchValue(value); setPage(1); };

  const handlePageSizeChange = (e) => { setPerPage(parseInt(e.target.value, 10)); setPage(1); };

  const handleEdit = useCallback((id) => {
    setSelected([]);
    setEditingRowId(id);
    setEditFormData((prev) => {
      const row = data.find((r) => r.id === id);
      if (!row) return prev;
      const assignedToId =
        typeof row.assigned_to === 'object' && row.assigned_to !== null
          ? row.assigned_to?.id ?? ''
          : row.assigned_to ?? '';
      return {
        ...prev,
        [id]: {
          id,
          assigned_to: assignedToId,
          environment: row.environment?.id || '',
        },
      };
    });
  }, [data]);

  const handleDropdownChange = (value, fieldName, rowId) => {
    setEditFormData((prev) => ({
      ...prev,
      [rowId]: { ...prev[rowId], [fieldName]: value },
    }));
  };

  const handleSave = useCallback(async () => {
    const fields = editFormData[editingRowId];
    if (!fields) return;
    const associations = [{ id: editingRowId, assignedTo: fields.assigned_to || null, environment: fields.environment || null }];
    try {
      await bulkUpdateAssociations({ associations }, entityId);
      setEditingRowId(null);
      setSelected([]);
      fetchLinkedTestCases(page, debouncedSearchQuery, selectedTestSuite);
      addNotification('success', 'Test cases updated successfully!');
    } catch (error) {
      addNotification('danger', error?.message || 'Failed to update test cases.');
    }
  }, [editFormData, editingRowId, entityId, page, debouncedSearchQuery, selectedTestSuite]);

  const handleCancel = useCallback(() => { setEditingRowId(null); setSelected([]); }, []);

  const handleDelete = async () => {
    try {
      await removeAssociationsBulk({ testcases: selected }, projectId);
      setShowConfirm(false);
      setSelected([]);
      setEditingRowId(null);
      fetchLinkedTestCases(page, debouncedSearchQuery, selectedTestSuite);
      addNotification('success', 'Test cases deleted successfully!');
    } catch (error) {
      addNotification('danger', error?.message || 'Failed to delete test cases.');
    }
  };

  const handleBulkDropdownChange = (value, fieldName) => {
    setEditFormData((prev) => {
      const patch = {};
      selected.forEach((id) => { patch[id] = { ...prev[id], [fieldName]: value }; });
      return { ...prev, ...patch };
    });
  };

  const handleBulkSave = async () => {
    const associations = selected.map((id) => ({
      id, assignedTo: editFormData[id]?.assigned_to || null, environment: editFormData[id]?.environment || null,
    }));
    try {
      await bulkUpdateAssociations({ associations }, entityId);
      setSelected([]);
      fetchLinkedTestCases(page, debouncedSearchQuery, selectedTestSuite);
      addNotification('success', 'Test cases updated successfully!');
    } catch (error) {
      addNotification('danger', error?.message || 'Failed to update test cases.');
    }
  };

  const formattedData = useMemo(() => data.map((row) => {
    const envs = row?.test_case_executions?.map((ex) => ex?.iteration_details?.environment_details?.name) || [];
    const iters = row?.test_case_executions?.map((ex) => ex?.iteration_details?.name) || [];
    const suites = (row?.testcase?.test_suites || [])
      .map((s) => testSuites.find((a) => a.id === s)?.name)
      .filter(Boolean)
      .join(', ');
    return {
      ...row,
      key: row?.testcase?.key,
      description: row?.testcase?.summary,
      assignedTo: showUserDisplayName(row?.assigned_to, users),
      environment: envs.length ? [...new Set(envs)].join(', ') : 'None',
      iterations: iters.length ? [...new Set(iters)].join(', ') : 'None',
      module: row?.testcase?.module_details?.name,
      suites: suites || '',
      executionStatus: row?.test_case_executions[0]?.status_details || 'No Executions',
    };
  }), [data, testSuites, users]);

  const columnsWithFormatting = useMemo(() =>
    testCycleTestCaseTableHeaders.map((header) => {
      switch (header.key) {
        case 'key':
          return {
            ...header,
            formatContent: (row) => (
              <MuiLink
                component={RouterLink}
                to={`/project/${projectId}/testcases/${row?.testcase?.key}`}
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {row.key}
              </MuiLink>
            ),
          };

        case 'description':
          return { ...header, linkToId: undefined };

        case 'assigned_to':
          return {
            ...header,
            formatContent: (row) =>
              editingRowId === row.id ? (
                <Box sx={{ minWidth: 200 }}>
                  <CustomSelect
                    value={editFormData[row.id]?.assigned_to ?? ''}
                    onChange={(val) => handleDropdownChange(val, 'assigned_to', row.id)}
                    options={userOptions}
                    placeholder="Select Tester"
                    renderOption={renderUserOption}
                  />
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  color={row.assignedTo === 'Not Assigned' ? 'text.disabled' : 'text.primary'}
                >
                  {row.assignedTo}
                </Typography>
              ),
          };

        case 'environment':
        case 'iterations':
          return {
            ...header,
            formatContent: (row) => (
              <Typography
                variant="body2"
                color={row[header.key] === 'None' ? 'text.disabled' : 'text.primary'}
              >
                {row[header.key]}
              </Typography>
            ),
          };

        case 'suites':
          return {
            ...header,
            formatContent: (row) => (
              <Typography
                variant="body2"
                color={!row.suites ? 'text.disabled' : 'text.primary'}
              >
                {row.suites || 'None'}
              </Typography>
            ),
          };

        case 'executionStatus':
          return {
            ...header,
            formatContent: (row) =>
              row.executionStatus === 'No Executions' ? (
                <Typography variant="body2" color="text.disabled">No Executions</Typography>
              ) : (
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Circle sx={{ fontSize: 12, color: row.executionStatus?.color || 'text.disabled' }} />
                  <Typography variant="body2">{row.executionStatus?.name}</Typography>
                </Stack>
              ),
          };

        default:
          return header;
      }
    }),
    [editingRowId, editFormData, userOptions, renderUserOption, projectId],
  );

  const action = useCallback((row) => {
    if (editingRowId === row.id) {
      return (
        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
          <Tooltip title="Save changes" arrow>
            <Check
              onClick={handleSave}
              sx={{ fontSize: 18, color: 'success.main', cursor: 'pointer' }}
            />
          </Tooltip>
          <Tooltip title="Cancel" arrow>
            <Close
              onClick={handleCancel}
              sx={{ fontSize: 18, color: 'text.secondary', cursor: 'pointer' }}
            />
          </Tooltip>
        </Stack>
      );
    }
    return (
      <ActionMenu
        tooltip="Row actions"
        items={[
          {
            label: 'Edit',
            icon: <Edit />,
            onClick: () => handleEdit(row.id),
            hidden: isExecutiveManager() || isCompleted,
          },
          {
            label: 'Remove',
            icon: <Delete />,
            onClick: () => { setSelected([row.id]); setShowConfirm(true); },
            color: 'error',
            dividerBefore: true,
            hidden: isExecutiveManager() || isCompleted,
          },
        ]}
      />
    );
  }, [editingRowId, isCompleted, handleEdit, handleSave, handleCancel]);

  const cardActions = (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      {originalTotalCount > 0 && (
        <Typography variant="body2" color="text.secondary">
          Total: {originalTotalCount}
        </Typography>
      )}
      {!isCompleted && !isExecutiveManager() && (
        <ActionButton
          intent="primary"
          size="medium"
          label="+ Test Cases"
          tooltip="Add existing test cases"
          onClick={() => setShowModal(true)}
        />
      )}
    </Stack>
  );

  const showBulkBar = !isExecutiveManager() && selected.length > 1;

  const bulkAssignedTo = useMemo(() => {
    if (!selected.length) return '';
    const values = selected.map((id) => editFormData[id]?.assigned_to ?? '');
    return values.every((v) => v === values[0]) ? values[0] : '';
  }, [selected, editFormData]);

  return (
    <>
      <DetailsCard title="Test Cases" actions={cardActions}>

        {!isExecutiveManager() && (
          <Box sx={{ mb: 2 }}>
            {data.length > 1 && selected.length < 2 && (
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderLeft: `4px solid ${tokens.colors.neutral[400]}`,
                  backgroundColor: tokens.colors.neutral[100],
                  borderRadius: `0 ${tokens.radius.xs}px ${tokens.radius.xs}px 0`,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Note: Select two or more test cases for bulk editing options.
                </Typography>
              </Box>
            )}

            {showBulkBar && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  p: 1.5,
                  borderRadius: `${tokens.radius.sm}px`,
                  border: `1px solid ${tokens.colors.neutral[200]}`,
                  backgroundColor: alpha(tokens.colors.primary[500], 0.04),
                }}
              >

                <ActionButton
                  intent="danger"
                  size="medium"
                  label="Delete"
                  icon={<Delete />}
                  onClick={() => setShowConfirm(true)}
                />

                <Box sx={{ minWidth: 220 }}>
                  <CustomSelect
                    value={bulkAssignedTo}
                    onChange={(val) => handleBulkDropdownChange(val, 'assigned_to')}
                    options={userOptions}
                    placeholder="Assign Tester"
                    renderOption={renderUserOption}
                  />
                </Box>

                <ActionButton
                  intent="primary"
                  size="medium"
                  label="Save"
                  loading={loading}
                  onClick={handleBulkSave}
                />
                <ActionButton
                  size="medium"
                  variant="outlined"
                  intent="secondary"
                  label="Cancel"
                  onClick={() => { setSelected([]); setEditFormData({}); }}
                />
              </Stack>
            )}
          </Box>
        )}
        <AddExistingTestCaseModal
          show={showModal}
          handleClose={() => setShowModal(false)}
          fetchLinkedTestCases={() => fetchLinkedTestCases(page, debouncedSearchQuery, selectedTestSuite)}
          entityId={entityId}
          linkedTestSuites={testCycle?.test_suites || []}
          fetchTestCycleData={fetchTestCycleData}
        />
        <LinkedTestCaseActions
          searchValue={searchValue}
          setSearchValue={handleSearch}
          testSuites={testSuites}
          selectedTestSuite={selectedTestSuite}
          setSelectedTestSuite={setSelectedTestSuite}
        />

        <TableComponent
          data={formattedData}
          headerTabs={columnsWithFormatting}
          page={page}
          perPage={perPage}
          setPerPage={handlePageSizeChange}
          showCheckbox={!isExecutiveManager()}
          isRowDisabled={() => isCompleted || isExecutiveManager()}
          action={action}
          defaultSort="key"
          order={order}
          setOrder={setOrder}
          selected={selected}
          setSelected={setSelected}
          showPages
          handlePageChange={handlePageChange}
          totalCount={totalCount}
          isLoading={loading}
          showCount
          showRowCount
          tablekey="linked test cases"
          striped
        />
      </DetailsCard>

      <ConfirmationModal
        show={showConfirm}
        handleConfirm={handleDelete}
        handleClose={() => setShowConfirm(false)}
        title="Remove Linked Test Cases"
        body="Are you sure you want to remove the selected test case(s) and previous executions?"
        loading={loading}
      />
    </>
  );
};

export default LinkedTestCasesComponent;
